import _ from 'lodash';
import filters from '../core/filters';

import infoBoard from '../core/infoboard';
import worldMap from '../core/map';
import translate from '../core/tools/translate';
import environment from '../environment';
import { CoreControl } from './CoreControl';
import { VectorType } from './VectorType';
import api from '../api';
import { FeaturePermission } from '../../constants/feature.permission.helper';
import { ContextEnum } from '../../enuns/context.enum';
import { GREENFIELD_Z_INDEX_OFFSET } from '../layers/NewGreenfieldLayer';
import layers from '../layers';

const LayerInfo = (name) => require('../info').get(name);
const LayerControls = (name) => require('./index').get(name);

export class NewGreenfieldControl extends CoreControl {
    constructor () {
        super({ name: 'NewGreenfield' });

        this._filterCallback = this._filterCallback.bind(this);
        this._changePeriod = this._changePeriod.bind(this);
        this._toggleLabel = this._toggleLabel.bind(this);
        this.createFilter = this.createFilter.bind(this);
        this.resizeIcon = this.resizeIcon.bind(this);
        this._change = this._change.bind(this);
        this.highlight = this.highlight.bind(this);
        this.unselect = this.unselect.bind(this);
        this.stackFeatures = this.stackFeatures.bind(this);
        this.stack = this.stack.bind(this);
        this.select = this.select.bind(this);
    }

    static getInstance () {
        if (!NewGreenfieldControl._instance) {
            NewGreenfieldControl._instance = new NewGreenfieldControl();
        }

        return NewGreenfieldControl._instance;
    }

    init () {
        this._tags = {
            tagName: {
                property: 'PointName',
                align: 'east',
                size: 9,
                bold: true,
                box: true
            },
            tagAreaName: {
                property: 'Nome',
                box: true,
                colorize: true,
                color: '#fff59d',
                bold: true,
                size: 9,
                offsetY: 60
            },
            tagTier: {
                property: 'TypeName',
                offsetY: -21,
                size: 10,
                colorize: true,
                color: '#ef9a9a',
                bold: true,
                box: true,
            },
            tagShowTypePoint: {
                property: 'PointTypeTag',
                align: 'south',
                size: 10,
                colorize: true,
                color: 'PointTypeColor',
                type: 'property',
                bold: true,
                box: true,
            }
        };

        const values = {
            disableSlider: true,
            help: translate(`Help.${this.options.name}`),
            id: 'layer-newgreenfield',
            input: 'checkbox',
            label: translate(`Common.${this.options.name}`),
            layer: this.getLayer,
            change: this._change,
            name: this.options.name,
            type: 'vector-points',
            filter: api.authorization.isAuthorized(FeaturePermission.LAYER_NEW_GREENFIELD.filterType)
                ? { enable: true, click: this.createFilter }
                : undefined,
            purge: this.purge,
            tab () {
                return $('#newgreenfield-tab-link');
            },
            resize: {
                name: `pinSize${this.options.name}`,
                change: this.resizeIcon,
                type: 'radio'
            },
            dropdown: [],
            checkbox: this._getCheckboxOptions()
        };

        values.dropdown.push({
            name: 'year',
            dataSource: _.map(environment.get('CONFIG.FILTERS.GREENFIELD_SCENARIO_YEARS'), (val) => ({ text: val, value: val })),
            index: _.indexOf(environment.get('CONFIG.FILTERS.GREENFIELD_SCENARIO_YEARS'), this.getLayer().options.remote.params.year),
            change: this._changePeriod
        });

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    _change (e) {
        try {
            api.general.log.action(environment._getContextEnum(this.options.name), e.checked ? 'enable' : 'disable');
            const layer = this.getLayer();

            if (e.checked) {
                layer.redraw();
            } else {
                layer.hide();
            }
        } catch (err) {
            console.log('ERROR: ', err);
        }
    }

    createFilter () {
        return filters.create({
            id: `filter-newgreenfield`,
            label: translate(`Common.NewGreenfield`),
            type: 'newgreenfield',
            direction: 'up',
            onSubmit: this._filterCallback
        });
    }

    _getCheckboxOptions () {
        const contents = [
            { name: 'tagName', label: translate('Action.ShowTagPointName') },
            { name: 'tagAreaName', label: translate('Action.ShowTagAreaName') },
            { name: 'tagTier', label: translate('Greenfield.ShowTier') },
            { name: 'tagShowTypePoint', label: translate('Greenfield.ShowPointType') },
        ];

        const res = {};
        contents.forEach(content => {
            res[content.label] = {
                name: content.name,
                change: isChecked => {
                    this._toggleLabel(isChecked, content.name);
                },
                style: {
                    change: tag => {
                        this._tags[content.name] = tag;
                        this._toggleLabel();
                    },
                    options: this._tags[content.name]
                }
            };
        });

        return res;
    }

    _getLayerTag () {
        if (!this.getControls().checkbox) { return undefined; }

        const tags = [];

        if ((this.getControls().checkbox.tagName || {}).status) {
            tags.push(_.assign({}, this._tags.tagName));
        }

        if ((this.getControls().checkbox.tagTier || {}).status) {
            tags.push(_.assign({}, this._tags.tagTier));
        }

        if ((this.getControls().checkbox.tagShowTypePoint || {}).status) {
            tags.push(_.assign({}, this._tags.tagShowTypePoint));
        }

        _.forEach(_.reject(tags, tag => tag.align === 'south'), (tag, index) => {
            tag.stackedRow = index;
        });

        return tags.length === 1 ? tags[0] : tags;
    }

    _getLayerAreaTag () {
        if (!this.getControls().checkbox) { return undefined; }

        const {
          tagAreaName,
        } = this.getControls().checkbox;
        const tags = [];

        if ((tagAreaName || {}).status) {
            tags.push(_.assign({}, this._tags.tagAreaName));
        }

        return tags.length === 1 ? tags[0] : tags;
    }

    _setLabel () {
        this.getLayer().setOptions({ tag: this._getLayerTag() });
        this.getLayer(worldMap.getCurrent(), 'NewGreenfieldArea').setOptions({ tag: this._getLayerAreaTag() });
    }

    _toggleLabel (isChecked, name) {
        api.general.log.action(ContextEnum.LayerGreenfield, `${isChecked ? 'Show' : 'Hide'} ${name}`);
        this._setLabel();
        if (this.getLayer().visible()) {
            this.getLayer().reload();
            this.getLayer(worldMap.getCurrent(), 'NewGreenfieldArea').reload();
        }
    }

    _changePeriod (year) {
        this.getLayer().options.remote.params.year = String(year);

        if (this.check() && this.getLayer().visible()) {
            api.general.log.action(ContextEnum.LayerGreenfield, 'Update Filter Greenfield Year', JSON.stringify({ Year: String(year) }));
            this.getLayer().redraw();
        }
    }

    _filterCallback (values) {
        const layerFilers = {
            IdPointTier: values.IdPointTier ? (_.isArray(values.IdPointTier.value) && _.size(values.IdPointTier.value)) ? values.IdPointTier.value : [] : [],
            IdPointType: values.IdPointType ? (_.isArray(values.IdPointType.value) && _.size(values.IdPointType.value)) ? values.IdPointType.value : [] : []
        };

        const filter = this.getLayerFilters();

        _.forEach(layerFilers, (val, key) => {
            if (_.size(val) || (_.isBoolean(val))) {
                filter.properties[key] = val;
            } else {
                delete filter.properties[key];
            }
        });

        this.setLayerFilters(filter);
        api.general.log.action(ContextEnum.LayerGreenfield, 'Filtro', JSON.stringify(filter));

        if (!this.check()) {
            this.check(true);
        }
    }

    getLayerFilters() {
        const filter = (this.getLayer().options.filter || {});

        if (!filter.properties) filter.properties = {};

        return filter;
    }

    setLayerFilters (filter) {
        this.getLayer().setOptions({ filter });

        if (this.check() && this.getLayer().visible()) {
            this.getLayer().reload();
        }
    }

    toggleVisibility (id, layer) {
        return this._instance.toggleVisibility(id, layer);
    }

    highlight (marker, event) {
        return this._instance.highlight(marker, event);
    }

    select(marker, type) {
        this._instance.select(marker, type);
    }

    unselect(marker, type) {
        this._instance.unselect(marker, type);
    }

    view (values) {
        return LayerInfo(this.options.name).create(values);
    }

    show (options = {}, features = this.getLayer().getCurrentFeatures()) {
        if ($('#newgreenfield-collapse').length === 0 || options.refresh) {
            if (options.empty) {
                this.view(null);
            } else {
                this.view(features);
            }
        }

        if (options.show) {
            infoBoard.open();
            $('#newgreenfield-tab-link').trigger('click');
            if (options.id) {
                const title = $(`#${this.options.name.toLocaleLowerCase()}-title-${options.id}`);
                return setTimeout(() => {
                    if (title[0] && !title[0].isOpen) {
                        title.trigger('select');
                    }

                    if (options.tab === 'summary') {
                        return $(`#button-${this.options.name.toLocaleLowerCase()}-summary-${options.id}`).trigger('click');
                    }
                }, 100);
            }
        }
    }

    stackFeatures (features, currentMap = worldMap.getCurrent()) {
        const stores = _.flatMap(['Dealerships', 'StoresConsortium'], name => {
            const network = (layers.getAll(currentMap._id) || {})[name];
            return network && network.visible() && network.layer ? network.layer.getLayers().filter(l => l.feature) : [];
        });
        const storesById = _.keyBy(stores, s => s.feature.properties.Id);
        const brownfield = (layers.getAll(currentMap._id) || {}).Brownfield;
        const brownfieldMarkers = brownfield && brownfield.visible() && brownfield.layer ? brownfield.layer.getLayers().filter(l => l.feature) : [];
        const brownfieldByStore = _.keyBy(brownfieldMarkers, l => l.feature.properties.IdStoreReference);
        const brownfieldTags = brownfield && brownfield.visible() ? _.castArray(LayerControls('Brownfield')._getLayerTag() || []) : [];
        const brownfieldSideRows = _.size(_.reject(brownfieldTags, tag => tag.align === 'south'));
        const brownfieldBottom = Math.max(
            32 + (_.some(brownfieldTags, tag => tag.align === 'south') ? 22 : 0),
            brownfieldSideRows ? 10 + (brownfieldSideRows * 22) : 0
        );
        const belowSymbology = _.find((this.getLayer(currentMap).options.symbology || {}).values, v => v.value === 'pi_stacked_below');
        const brownfieldSymbology = _.find((this.getLayer(currentMap).options.symbology || {}).values, v => v.value === 'pi_stacked_brownfield');

        if (belowSymbology) {
            belowSymbology.style.icon.iconAnchor = [13, -(brownfieldBottom + 2)];
        }

        if (brownfieldSymbology) {
            brownfieldSymbology.style.icon.iconAnchor = [13, -(_.some(brownfieldTags, tag => tag.align === 'south') ? 28 : 6)];
        }

        _.forEach(features, f => {
            const store = storesById[f.properties.IdStore];
            const isSamePlace = store && _.every([0, 1], i => Math.abs(store.feature.geometry.coordinates[i] - f.geometry.coordinates[i]) < 0.000001);

            const brownfieldMarker = brownfieldByStore[f.properties.IdStore];
            const isOnBrownfield = brownfieldMarker && _.every([0, 1], i => Math.abs(brownfieldMarker.feature.geometry.coordinates[i] - f.geometry.coordinates[i]) < 0.000001);

            if (isSamePlace) {
                f.properties['Icon'] = isOnBrownfield && brownfieldMarker.feature.properties.Icon === 'action_stacked' ? 'pi_stacked_below' : 'pi_stacked';
            } else {
                f.properties['Icon'] = isOnBrownfield ? 'pi_stacked_brownfield' : 'pi';
            }
        });
    }

    stack (currentMap = worldMap.getCurrent()) {
        if (!this.check() || !this.getLayer(currentMap).visible()) {
            return;
        }

        this.stackFeatures(this.getLayer(currentMap).getFeatures(), currentMap);
        this.getLayer(currentMap).reload();
    }

    resizeIcon (value) {
        const { symbology } = this.getLayer().options;
        switch (value) {
        case 'large':
            symbology.values = [{
                value: 'pi',
                style: {
                    icon: {
                        fields: ['TierColor'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}.png`,
                        iconSize: [50, 64], // size of the icon
                        iconAnchor: [25, 60], // point of the icon which will correspond to marker's location
                        labelAnchor: [25, -32]
                    },
                    zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }, {
                value: 'pi_stacked',
                style: {
                    icon: {
                        fields: ['TierColor'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                        iconSize: [26, 26],
                        iconAnchor: [13, -6],
                        labelAnchor: [6, 0],
                        stacked: true
                    },
                    zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }, {
                value: 'pi_stacked_below',
                style: {
                    icon: {
                        fields: ['TierColor'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                        iconSize: [26, 26],
                        iconAnchor: [13, -34],
                        labelAnchor: [6, 0],
                        stacked: true
                    },
                    zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }, {
                value: 'pi_stacked_brownfield',
                style: {
                    icon: {
                        fields: ['TierColor'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                        iconSize: [26, 26],
                        iconAnchor: [13, -6],
                        labelAnchor: [6, 0],
                        stacked: true
                    },
                    zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }];
            break;

        case 'medium':
            symbology.values = [{
                value: 'pi',
                style: {
                    icon: {
                        fields: ['TierColor'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_sm.png`,
                        iconSize: [35, 45], // size of the icon
                        iconAnchor: [18, 45], // point of the icon which will correspond to marker's location
                        labelAnchor: [18, -24]
                    },
                    zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }, {
                value: 'pi_stacked',
                style: {
                    icon: {
                        fields: ['TierColor'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                        iconSize: [26, 26],
                        iconAnchor: [13, -6],
                        labelAnchor: [6, 0],
                        stacked: true
                    },
                    zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }, {
                value: 'pi_stacked_below',
                style: {
                    icon: {
                        fields: ['TierColor'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                        iconSize: [26, 26],
                        iconAnchor: [13, -34],
                        labelAnchor: [6, 0],
                        stacked: true
                    },
                    zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }, {
                value: 'pi_stacked_brownfield',
                style: {
                    icon: {
                        fields: ['TierColor'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                        iconSize: [26, 26],
                        iconAnchor: [13, -6],
                        labelAnchor: [6, 0],
                        stacked: true
                    },
                    zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }];
            break;

        case 'small':
            symbology.values = [{
                value: 'pi',
                style: {
                    icon: {
                        fields: ['TierColor'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                        iconSize: [26, 26], // size of the icon
                        iconAnchor: [13, 13], // point of the icon which will correspond to marker's location
                        labelAnchor: [6, 0]
                    },
                    zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }, {
                value: 'pi_stacked',
                style: {
                    icon: {
                        fields: ['TierColor'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                        iconSize: [26, 26],
                        iconAnchor: [13, -6],
                        labelAnchor: [6, 0],
                        stacked: true
                    },
                    zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }, {
                value: 'pi_stacked_below',
                style: {
                    icon: {
                        fields: ['TierColor'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                        iconSize: [26, 26],
                        iconAnchor: [13, -34],
                        labelAnchor: [6, 0],
                        stacked: true
                    },
                    zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }, {
                value: 'pi_stacked_brownfield',
                style: {
                    icon: {
                        fields: ['TierColor'],
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{TierColor}_xs.png`,
                        iconSize: [26, 26],
                        iconAnchor: [13, -6],
                        labelAnchor: [6, 0],
                        stacked: true
                    },
                    zIndexOffset: GREENFIELD_Z_INDEX_OFFSET,
                    riseOnHover: true
                }
            }];
            break;
        }

        this.getLayer().setOptions({ symbology });
        if (this.check() && this.getLayer().visible()) {
            this.stackFeatures(this.getLayer().getFeatures());
            return this.getLayer().reload();
        }
    }
}

export default NewGreenfieldControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/NewGreenfieldControl.js